import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import LoadingRing from 'ui/atoms/loading-ring';
import useApiCall from 'hooks/use-api-call';
import { InvestmentsApi, TwoFactorAuthApi } from 'api/apis';
import WizardContext from 'libraries/wizard/wizard-context';
import Translate from 'ui/atoms/translate';
import { getWalletAddressForNetwork } from 'ui/helper/networks';
import UpdateMobileSafekeeping from 'src/subapps/investment/pages/investment/wizard-steps/wallet/setup-mobile-wallet/update-mobile-safekeeping';
import { StepEnum, WalletTypeEnum } from 'api/models';
import useInvestorMe from 'hooks/use-investor-me';
import CreateMobileSafekeepingWallet from 'subapps/investment/pages/investment/wizard-steps/wallet/setup-mobile-wallet/create-mobile-safekeeping-wallet';
import WizardHeader from 'libraries/wizard/wizard-header';
import Header from 'ui/atoms/header';

const SetupMobileWalletStep: FunctionComponent<{}> = () => {
  const { investmentId, token, reload, finalize, changeStep } = useContext(WizardContext);

  const { investor, error: investorError } = useInvestorMe();

  const [isMobileWallet, setMobileWallet] = useState<boolean | undefined>();

  const { error, makeAuthenticatedApi, withApi } = useApiCall();

  const investmentsApi: InvestmentsApi = useMemo(() => makeAuthenticatedApi(InvestmentsApi), [makeAuthenticatedApi]);

  let safekeepingAppUpdateNeeded = false;

  const isMobileWalletRef = useRef(isMobileWallet);

  useEffect(() => {
    isMobileWalletRef.current = isMobileWallet;
  }, [isMobileWallet]);

  useEffect(() => {
    withApi(async () => {
      let inv = await investmentsApi.investmentsRetrieve({ id: investmentId });
      const isMobile = inv.addWalletLater === WalletTypeEnum.MOBILE;
      setMobileWallet(isMobile);
    });
  }, [withApi, investmentsApi, investmentId]);

  const load = useCallback(() => {
    withApi(async () => {
      let inv = await investmentsApi.investmentsRetrieve({
        id: investmentId,
      });

      // Wizard step: SETUPMOBILEWALLET && investment has wallet -> app needs update
      safekeepingAppUpdateNeeded = !!inv?.wallet;

      const appUpdateSuccessfull = safekeepingAppUpdateNeeded && inv.step !== StepEnum.SETUP_MOBILE_WALLET;

      const walletSetupSuccessful =
        !safekeepingAppUpdateNeeded &&
        token?.tokenizationInfo?.networkType &&
        inv?.wallet &&
        getWalletAddressForNetwork(token?.tokenizationInfo.networkType, inv.wallet);

      if (appUpdateSuccessfull || walletSetupSuccessful) {
        reload();

        finalize();
      }
    });
  }, [withApi, investmentsApi, reload, finalize, investmentId, token]);

  const twoFactorAuthApi: TwoFactorAuthApi = useMemo(
    () => makeAuthenticatedApi(TwoFactorAuthApi),

    [makeAuthenticatedApi],
  );

  const sendMobileSafeKeepingSetupEmail = useCallback(() => {
    withApi(async () => {
      await twoFactorAuthApi.twoFactorAuthDeviceInitializationCreate({
        tFADeviceInitializationRequest: {
          walletNeeded: isMobileWalletRef.current,
        },
      });
    });
  }, [withApi, twoFactorAuthApi]);

  const resetWalletOption = useCallback(() => {
    withApi(async () => {
      try {
        await investmentsApi.investmentsResetWalletSelectionCreate({
          id: investmentId,
        });

        changeStep(StepEnum.SELECT_WALLET);
      } catch (e) {
        console.error(e);
      }
    });
  }, [withApi]);

  const timeoutHandler = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    timeoutHandler.current = setInterval(load, 2000);

    return () => {
      if (timeoutHandler.current) {
        clearInterval(timeoutHandler.current);
      }
    };
  }, [load]);

  useEffect(() => {
    if (error && timeoutHandler.current) {
      clearInterval(timeoutHandler.current);
    }
  }, [error, timeoutHandler]);

  if (!investor) return <LoadingRing />;

  const title = safekeepingAppUpdateNeeded ? (
    <Translate name="updateMobileSafekeeping.title" />
  ) : (
    <Translate name="investmentDepot.title" />
  );

  return (
    <>
      <WizardHeader />

      <Header size="large" spacing="xlarge">
        {title}
      </Header>

      {safekeepingAppUpdateNeeded ? (
        <UpdateMobileSafekeeping />
      ) : (
        <CreateMobileSafekeepingWallet
          email={investor.email}
          onResendEmail={sendMobileSafeKeepingSetupEmail}
          error={error || investorError}
          resetWalletOption={resetWalletOption}
        />
      )}
    </>
  );
};

export default SetupMobileWalletStep;
